import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  HiOutlineArrowRightOnRectangle,
  HiOutlineBars3,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineKey,
} from "react-icons/hi2";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getBreadcrumbs, NAV_GROUPS } from "../../app/navigation";
import { adminEnvironment } from "../../config/environment";
import useAuth from "../../hooks/useAuth";
import useLocale from "../../hooks/useLocale";
import { getRoleLabel, getScopeLabel } from "../../utils/access";
import cn from "../../ui/cn";
import LanguageSwitcher from "../LanguageSwitcher";

const getInitials = (user = {}) => {
  const first = String(user.firstName || "").trim().charAt(0);
  const last = String(user.lastName || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "BW";
};

function Header({ onOpenSidenav, openButtonRef, showMenuButton }) {
  const { t } = useTranslation();
  const { isRTL } = useLocale();
  const { auth, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const trail = useMemo(() => getBreadcrumbs(location.pathname), [location.pathname]);
  // The page itself renders the H1; the header shows where the page sits in
  // the navigation (group / section / page) instead of repeating the title.
  const group = NAV_GROUPS.find((item) => item.id === trail[0]?.group);
  const crumbs = [
    ...(group ? [{ label: t(group.labelKey) }] : []),
    ...trail.map((page, index) => ({
      label: t(page.titleKey),
      to: index < trail.length - 1 && !page.path.includes(":") ? page.path : undefined,
    })),
  ];

  const organizationName = user?.platform?.name || user?.agency?.name || auth?.organizationName || "-";
  const organizationType = (user?.platform ? "platform" : "") || (user?.agency ? "agency" : "") || auth?.organizationType;
  const userRole = user?.role || auth?.role;
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || getRoleLabel(userRole, t);
  const isProduction = adminEnvironment.environment === "production";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          {showMenuButton ? (
            <button
              ref={openButtonRef}
              type="button"
              onClick={onOpenSidenav}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
              aria-label={t("header.openNavigation")}
              aria-controls="admin-sidebar"
            >
              <HiOutlineBars3 size={22} aria-hidden="true" />
            </button>
          ) : null}
          <nav aria-label={t("shell.breadcrumb")} className="min-w-0">
            <ol className="flex min-w-0 items-center gap-1.5 text-sm">
              {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                return (
                  <li key={`${crumb.label}-${index}`} className={cn("flex min-w-0 items-center gap-1.5", !isLast && "hidden sm:flex")}>
                    {index > 0 ? <HiOutlineChevronRight size={13} className="rtl-flip shrink-0 text-slate-300" aria-hidden="true" /> : null}
                    {crumb.to ? (
                      <Link to={crumb.to} className="truncate text-slate-500 hover:text-brand-700 hover:underline">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span
                        className={cn("truncate", isLast ? "font-semibold text-slate-900" : "text-slate-500")}
                        aria-current={isLast ? "page" : undefined}
                        data-testid={isLast ? "page-title" : undefined}
                      >
                        {crumb.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div
            className={cn(
              "hidden items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide lg:flex",
              isProduction ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-800",
            )}
            title={`${t("shell.apiTarget")}: ${adminEnvironment.apiHost}`}
            data-testid="admin-environment-identity"
          >
            <span className={cn("h-2 w-2 rounded-full", isProduction ? "bg-rose-500" : "bg-amber-500")} aria-hidden="true" />
            <span>{adminEnvironment.environment}</span>
            <span className="ltr-value max-w-44 truncate font-normal normal-case tracking-normal opacity-80">
              {adminEnvironment.apiHost}
            </span>
          </div>

          <div className="hidden sm:block">
            <LanguageSwitcher compact />
          </div>

          <Menu>
            <MenuButton
              className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white ps-1 pe-2 text-start transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-200"
              aria-label={t("header.profileMenu")}
              data-testid="profile-menu-button"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-700 text-xs font-bold text-white">
                {getInitials(user || {})}
              </span>
              <span className="hidden max-w-36 md:block">
                <span className="block truncate text-xs font-semibold text-slate-900">{fullName}</span>
                <span className="block truncate text-[11px] text-slate-500">{getRoleLabel(userRole, t)}</span>
              </span>
              <HiOutlineChevronDown size={15} className="text-slate-400" aria-hidden="true" />
            </MenuButton>
            <MenuItems
              anchor={{ to: isRTL ? "bottom start" : "bottom end", gap: 8 }}
              transition
              className="z-[80] w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-overlay outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="truncate text-sm font-semibold text-slate-900">{fullName}</p>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {getScopeLabel(organizationType, t)} · {organizationName}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t("shell.profile.signedInAs")} {getRoleLabel(userRole, t)}
                </p>
              </div>
              <div className="border-b border-slate-100 px-4 py-3 sm:hidden">
                <LanguageSwitcher />
              </div>
              <div className="p-1.5">
                <MenuItem>
                  {({ focus }) => (
                    <Link
                      to="/Profile/Password"
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-slate-700",
                        focus && "bg-slate-100",
                      )}
                    >
                      <HiOutlineKey size={17} aria-hidden="true" />
                      {t("header.changePassword")}
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start text-sm text-rose-700",
                        focus && "bg-rose-50",
                      )}
                      data-testid="logout-button"
                    >
                      <HiOutlineArrowRightOnRectangle size={17} className="rtl-flip" aria-hidden="true" />
                      {t("header.logOut")}
                    </button>
                  )}
                </MenuItem>
              </div>
              <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-500 lg:hidden">
                {t("shell.profile.environment")}: {adminEnvironment.environment} ·{" "}
                <span className="ltr-value">{adminEnvironment.apiHost}</span>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </div>
    </header>
  );
}

export default Header;
